import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Table } from 'react-bootstrap';
import dayjs from 'dayjs';
import { buildCategoryRows, formatCost } from './costHelpers';
import IpfItemsDetailModal from './IpfItemsDetailModal';

function ClickableCell({ value, items, onOpenDetail }) {
  return (
    <td
      className="cost-ipf-cell-clickable"
      role="button"
      tabIndex={0}
      onClick={() => onOpenDetail(items)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpenDetail(items);
        }
      }}
    >
      {value}
    </td>
  );
}

ClickableCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  items: PropTypes.array.isRequired,
  onOpenDetail: PropTypes.func.isRequired
};

export default function IpfCategoryTable({ title, records, days, category }) {
  const { rows, totals } = useMemo(() => buildCategoryRows(records, category, days), [records, category, days]);
  const [detail, setDetail] = useState(null);

  const openDetail = (label, items) => setDetail({ title: `${title} — ${label}`, items });

  return (
    <div className="cost-ipf-table-wrapper" style={{ borderRadius: 6, padding: 16 }}>
      <h4 className="cost-ipf-table-title" style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700, textAlign: 'center', textTransform: 'uppercase' }}>
        {title}
      </h4>
      <Table striped bordered hover size="sm" responsive>
        <thead>
          <tr>
            <th>Date</th>
            <th>White</th>
            <th>Total Cost</th>
            <th>Foiled</th>
            <th>Total Cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.date}>
              <td>{dayjs(row.date, 'YYYY-MM-DD').format('DD/MM/YYYY')}</td>
              <ClickableCell value={row.whiteCount} items={row.whiteItems} onOpenDetail={items => openDetail(`White — ${dayjs(row.date, 'YYYY-MM-DD').format('DD/MM/YYYY')}`, items)} />
              <ClickableCell
                value={formatCost(row.whiteTotalCost)}
                items={row.whiteItems}
                onOpenDetail={items => openDetail(`White Cost — ${dayjs(row.date, 'YYYY-MM-DD').format('DD/MM/YYYY')}`, items)}
              />
              <ClickableCell value={row.foiledCount} items={row.foiledItems} onOpenDetail={items => openDetail(`Foiled — ${dayjs(row.date, 'YYYY-MM-DD').format('DD/MM/YYYY')}`, items)} />
              <ClickableCell
                value={formatCost(row.foiledTotalCost)}
                items={row.foiledItems}
                onOpenDetail={items => openDetail(`Foiled Cost — ${dayjs(row.date, 'YYYY-MM-DD').format('DD/MM/YYYY')}`, items)}
              />
            </tr>
          ))}
          <tr style={{ fontWeight: 700 }}>
            <td>{totals.date}</td>
            <ClickableCell value={totals.whiteCount} items={totals.whiteItems} onOpenDetail={items => openDetail('White — Total', items)} />
            <ClickableCell value={formatCost(totals.whiteTotalCost)} items={totals.whiteItems} onOpenDetail={items => openDetail('White Cost — Total', items)} />
            <ClickableCell value={totals.foiledCount} items={totals.foiledItems} onOpenDetail={items => openDetail('Foiled — Total', items)} />
            <ClickableCell value={formatCost(totals.foiledTotalCost)} items={totals.foiledItems} onOpenDetail={items => openDetail('Foiled Cost — Total', items)} />
          </tr>
        </tbody>
      </Table>

      <IpfItemsDetailModal show={!!detail} onHide={() => setDetail(null)} title={detail?.title || ''} items={detail?.items || []} />
    </div>
  );
}

IpfCategoryTable.propTypes = {
  title: PropTypes.string.isRequired,
  records: PropTypes.array.isRequired,
  days: PropTypes.arrayOf(PropTypes.string).isRequired,
  category: PropTypes.string.isRequired
};
